import { produce } from 'immer';

import {
    ADD_PHRASES_START_INPUT_ITEMS,
    CLOSE_PHRASES_SELECTOR_WIZARD,
    FETCH_PHRASES_FOR_PHRASES_SELECTOR_WIZARD,
    OPEN_PHRASES_SELECTOR_WIZARD,
    PHRASES_SELECTOR_ADD_PHRASES_TO_CHOOSEN_LIST,
    PHRASES_SELECTOR_ADD_PHRASES_TO_MINUS_WORDS_LIST,
    PHRASES_SELECTOR_CHANGE_COLUMNS_VISIBILITY,
    PHRASES_SELECTOR_CHANGE_INPUT,
    PHRASES_SELECTOR_CHANGE_PRICE_CONTEXT,
    PHRASES_SELECTOR_DELETE_START_PHRASES,
    PHRASES_SELECTOR_REMOVE_PHRASES_FROM_CHOOSEN_LIST,
    PHRASES_SELECTOR_REMOVE_PHRASES_FROM_MINUS_WORDS_LIST,
    PHRASES_SELECTOR_SELECTION_CHANGE,
    PHRASES_SELECTOR_SEND_MINUS_PHRASE_DATA,
    PHRASES_SELECTOR_SUBMIT_SELECTION_WIZARD,
    PhrasesSelectorWizardAction,
    SelectionWizardListName,
    SHOW_PHRASES_SELECTOR_WIZARD,
    UPDATE_PHRASES_SELECTOR_WIZARD_STATE,
    UPDATE_PHRASES_WIZARD_ERROR
} from '../actions/phrasesSelectorWizard';
import { getNewChosenWordsList, getNewMinusWordsList } from '../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { GroupType } from '../typings/domain';
import {
    PhrasesSelectorInputColumns,
    PhrasesSelectorListItem,
    PhrasesSelectorWizardId,
    PhrasesSelectorWizardState
} from '../typings/phrases';
import { PhrasesSelectorWizardsState, State } from '../typings/state';

const phrasesSelectorWizardInitialState = {
    groupInfo: {
        id: '',
        minusKeywords: [],
        regionIds: [],
        name: '',
        groupType: GroupType.TEXT,
        campaign: {
            name: '',
            id: '',
            strategy: {
                isAutoBudget: false
            }
        }
    },

    inputValue: {
        [PhrasesSelectorInputColumns.SEARCH_VALUE]: '',
        [PhrasesSelectorInputColumns.CHOSEN_WORDS]: '',
        [PhrasesSelectorInputColumns.MINUS_WORDS]: ''
    },

    errors: {
        [PhrasesSelectorInputColumns.SEARCH_VALUE]: undefined,
        [PhrasesSelectorInputColumns.CHOSEN_WORDS]: undefined,
        [PhrasesSelectorInputColumns.MINUS_WORDS]: undefined
    },

    isVisible: false,
    isInProgress: false,
    startPhrases: [],
    chosenPhrases: [],
    minusWordsList: [],
    keywordsStat: {},
    phrasesSelection: {},
    columnsVisibility: {
        [SelectionWizardListName.similarPhrases]: true,
        [SelectionWizardListName.chosenPhrases]: true
    },
    priceContext: undefined
};

export const getPhrasesSelectorWizardInitialState = (): PhrasesSelectorWizardState => phrasesSelectorWizardInitialState;

export function phrasesSelectorWizard(
    state: PhrasesSelectorWizardsState = {
        [PhrasesSelectorWizardId.sideBar]: getPhrasesSelectorWizardInitialState(),
        [PhrasesSelectorWizardId.editGroupsPage]: getPhrasesSelectorWizardInitialState(),
        [PhrasesSelectorWizardId.groupsGrid]: getPhrasesSelectorWizardInitialState()
    },
    action: PhrasesSelectorWizardAction,
    fullState: State
): PhrasesSelectorWizardsState {
    const wizardId = action.wizardId;
    if (!wizardId) {
        return state;
    }

    const wizardState = state[wizardId];

    const { MINUS_WORDS, CHOSEN_WORDS } = PhrasesSelectorInputColumns;

    switch (action.type) {
        case UPDATE_PHRASES_SELECTOR_WIZARD_STATE: {
            const keywordsStat = {
                ...wizardState.keywordsStat,
                ...(action.state.keywordsStat || {})
            };

            const startPhrases = action.state.startPhrases || wizardState.startPhrases;

            return produce(state, draft => {
                draft[wizardId].keywordsStat = keywordsStat;
                draft[wizardId].startPhrases = startPhrases;
                draft[wizardId].isInProgress = action.state.isInProgress;
            });
        }
        case PHRASES_SELECTOR_ADD_PHRASES_TO_CHOOSEN_LIST: {
            const chosenPhrases = getNewChosenWordsList(wizardState.chosenPhrases, action.items);

            return produce(state, draft => {
                draft[wizardId].chosenPhrases = chosenPhrases;
                draft[wizardId].inputValue[CHOSEN_WORDS] = '';
            });
        }
        case PHRASES_SELECTOR_REMOVE_PHRASES_FROM_CHOOSEN_LIST: {
            const chosenPhrases = wizardState.chosenPhrases.filter(
                phrase => !action.items.some((el: PhrasesSelectorListItem) => el.text === phrase.text)
            );

            return produce(state, draft => {
                draft[wizardId].chosenPhrases = chosenPhrases;
            });
        }

        case PHRASES_SELECTOR_DELETE_START_PHRASES: {
            const startPhrases = wizardState.startPhrases.filter(
                phrase => !action.items.some((el: PhrasesSelectorListItem) => el.text === phrase.text)
            );

            return produce(state, draft => {
                draft[wizardId].startPhrases = startPhrases;
            });
        }

        case PHRASES_SELECTOR_SELECTION_CHANGE: {
            const list = wizardState[action.listName] as PhrasesSelectorListItem[];

            const phrasesSelection = {
                ...wizardState.phrasesSelection
            };

            list.forEach(({ text }: PhrasesSelectorListItem, index) => {
                phrasesSelection[text] = action.selection[index];
            });

            const newList = list.map((el: PhrasesSelectorListItem) => ({
                ...el,
                selected: phrasesSelection[el.text]
            }));

            return produce(state, draft => {
                draft[wizardId].phrasesSelection = phrasesSelection;
                draft[wizardId][action.listName] = newList;
            });
        }

        case PHRASES_SELECTOR_REMOVE_PHRASES_FROM_MINUS_WORDS_LIST: {
            const minusWordsList = wizardState.minusWordsList.filter(
                phrase => !action.items.some(el => el.text === phrase.text)
            );

            return produce(state, draft => {
                draft[wizardId].isInProgress = true;
                draft[wizardId].minusWordsList = minusWordsList;
            });
        }

        case PHRASES_SELECTOR_ADD_PHRASES_TO_MINUS_WORDS_LIST: {
            const minusWordsList = getNewMinusWordsList(
                wizardState.startPhrases,
                wizardState.minusWordsList,
                action.items
            );

            return produce(state, draft => {
                draft[wizardId].isInProgress = false;
                draft[wizardId].minusWordsList = minusWordsList;
                draft[wizardId].inputValue[MINUS_WORDS] = '';
            });
        }

        case OPEN_PHRASES_SELECTOR_WIZARD: {
            const { startPhrases } = action.groupInfo;

            return produce(state, draft => {
                draft[wizardId].groupInfo = action.groupInfo;
                draft[wizardId].startPhrases = startPhrases || [];
                draft[wizardId].chosenPhrases = [];
                draft[wizardId].minusWordsList = [];
                draft[wizardId].priceContext = phrasesSelectorWizardInitialState.priceContext;
            });
        }

        case SHOW_PHRASES_SELECTOR_WIZARD: {
            return produce(state, draft => {
                draft[wizardId].isVisible = true;
            });
        }

        case CLOSE_PHRASES_SELECTOR_WIZARD: {
            return produce(state, draft => {
                draft[wizardId].isInProgress = false;
                draft[wizardId].isVisible = false;
                draft[wizardId].inputValue = phrasesSelectorWizardInitialState.inputValue;
                draft[wizardId].errors = phrasesSelectorWizardInitialState.errors;
            });
        }

        case ADD_PHRASES_START_INPUT_ITEMS: {
            return produce(state, draft => {
                draft[wizardId].isInProgress = true;
            });
        }

        case FETCH_PHRASES_FOR_PHRASES_SELECTOR_WIZARD: {
            return produce(state, draft => {
                draft[wizardId].isInProgress = true;
            });
        }

        case PHRASES_SELECTOR_CHANGE_COLUMNS_VISIBILITY: {
            return produce(state, draft => {
                draft[wizardId].columnsVisibility = {
                    ...draft[wizardId].columnsVisibility,
                    ...action.value
                };
            });
        }

        case PHRASES_SELECTOR_CHANGE_PRICE_CONTEXT: {
            return produce(state, draft => {
                draft[wizardId].priceContext = action.value;
            });
        }

        case PHRASES_SELECTOR_SUBMIT_SELECTION_WIZARD: {
            return produce(state, draft => {
                draft[wizardId].isInProgress = true;
            });
        }

        case PHRASES_SELECTOR_CHANGE_INPUT: {
            return produce(state, draft => {
                draft[wizardId].inputValue[action.displayIn] = action.value;
                draft[wizardId].errors[action.displayIn] = undefined;
            });
        }

        case PHRASES_SELECTOR_SEND_MINUS_PHRASE_DATA: {
            return produce(state, draft => {
                draft[wizardId].isInProgress = true;
            });
        }

        case UPDATE_PHRASES_WIZARD_ERROR: {
            return produce(state, draft => {
                draft[wizardId].errors[action.displayIn] = action.error;
            });
        }

        default:
            return state;
    }
}
